import PT from 'prop-types';

import { getWorkflowConstants } from '~/common/constants/workflow';
import {
  getFieldColor,
  getFieldTypeIcon,
} from '~/components/Workflow/Workflow.utils';
import { Icon as EdsIcon } from '~/eds/src/components/Icon/Icon';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { intlService } from '~/i18n/IntlService';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Text from '~/ui/components/Text/Text';

function getIconColorLabelValues(field, fieldState, intl) {
  const { fieldTypeLabels } = getWorkflowConstants(intl);
  switch (fieldState) {
    case 'WARNING': {
      return {
        icon: 'warning',
        iconBg: 'yellow-500',
        iconColor: 'white',
        label: null,
        labelColor: 'gray-600',
        nameColor: 'peach-800',
      };
    }
    case 'ASSIGNED': {
      const fieldColor = getFieldColor(field);
      const fieldIcon = getFieldTypeIcon(field);
      return {
        icon: fieldIcon,
        iconBg: fieldColor,
        iconColor: 'white',
        label: fieldTypeLabels[field.type],
        labelColor: fieldColor,
      };
    }
    default: {
      const intl = intlService.getIntl();
      return {
        icon: 'help',
        iconBg: 'gray-500',
        iconColor: 'gray-200',
        label: intl.formatMessage({
          id: 'Field.unassignedLabel',
          defaultMessage: 'unassigned',
          description: 'Label for fields that are not assigned in workflow',
        }),
        labelColor: 'gray-600',
      };
    }
  }
}

function Field({
  assignedLabel,
  disabled = false,
  field,
  isAssigned,
  shouldHideLabel = false,
  warningTooltip,
  errorMessage,
}) {
  let fieldState;
  if (warningTooltip) {
    fieldState = 'WARNING';
  } else if (isAssigned) {
    fieldState = 'ASSIGNED';
  }

  const intl = useIntl();

  const { icon, iconBg, iconColor, label, labelColor, nameColor } =
    getIconColorLabelValues(field, fieldState, intl);

  return (
    <Tooltip tooltip={warningTooltip}>
      <FlexLayout justifyContent="space-between">
        <FlexLayout disabled={disabled} alignItems="center" space={4}>
          <Icon
            bg={iconBg}
            color={iconColor}
            icon={icon}
            p={1}
            size="l"
            sx={{ borderRadius: 'm' }}
          />
          <Box>
            <Text color={nameColor} variant="xs-dense-bold">
              {field.name}
            </Text>
            {!shouldHideLabel && (
              <FlexLayout alignItems="center" space={1}>
                <Text color={labelColor} variant="2xs-dense-caps">
                  {assignedLabel || label}
                </Text>
              </FlexLayout>
            )}
          </Box>
        </FlexLayout>
        {errorMessage && (
          <EdsIcon
            icon="status-warning"
            tooltip={errorMessage}
            color="status.warning"
          />
        )}
      </FlexLayout>
    </Tooltip>
  );
}

const fieldPropTypes = PT.shape({
  id: PT.string,
  description: PT.string,
  name: PT.string,
  type: PT.string,
});

Field.propTypes = {
  assignedLabel: PT.string,
  field: fieldPropTypes.isRequired,
  isAssigned: PT.bool,
  shouldHideLabel: PT.bool,
  warningTooltip: PT.string,
};

export default Field;
